import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { authFlows } from '../authFlows';
import './auth.css';

/* Form card only — AuthPage owns the page layout, brand side, and the
   animated switch between this and the login form. */
export default function RegisterForm({ onSwitch }) {
  const [form, setForm] = useState({ companyName: '', name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const companyName = form.companyName.trim().slice(0, 100);
    const name = form.name.trim().slice(0, 100);
    const email = form.email.trim().toLowerCase();
    const { password } = form;

    if (!companyName || !name || !email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    // Keep App.js's auth listener quiet until both docs exist — otherwise it
    // reads the missing user doc right after createUser and rejects the account.
    authFlows.registering = true;
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      // Write docs sequentially so both exist before re-auth fires
      await setDoc(doc(db, 'users', uid), {
        name,
        email,
        role: 'admin',
        companyId: uid,
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'companies', uid), {
        companyName,
        adminName: name,
        adminEmail: email,
        createdAt: serverTimestamp(),
        locked: false,
        lockedAt: null,
      });

      // Docs are in place — let the listener process the re-login normally.
      authFlows.registering = false;
      // Sign out then back in so onAuthStateChanged sees the fully-written user doc
      await signOut(auth);
      await signInWithEmailAndPassword(auth, email, password);
      // App.js will now find the doc and route to dashboard
    } catch (err) {
      authFlows.registering = false;
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  const rise = (i) => ({ animationDelay: `${0.05 + i * 0.06}s` });

  return (
    <>
      <div className="auth-eyebrow auth-rise" style={rise(0)}>
        5-day free trial &middot; no credit card
      </div>
      <h1 className="auth-title auth-rise" style={rise(1)}>
        Set up <em>your company.</em>
      </h1>
      <p className="auth-subtitle auth-rise" style={rise(2)}>
        Two minutes from now, your team has a clock-in system.
      </p>

      {error && <div className="auth-error" role="alert">{error}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="auth-field auth-rise" style={rise(3)}>
          <label className="auth-label" htmlFor="companyName">Company name</label>
          <input
            id="companyName"
            className="auth-input"
            type="text"
            placeholder="Acme Corp"
            value={form.companyName}
            onChange={set('companyName')}
            autoComplete="organization"
            required
          />
        </div>
        <div className="auth-field auth-rise" style={rise(4)}>
          <label className="auth-label" htmlFor="name">Your full name</label>
          <input
            id="name"
            className="auth-input"
            type="text"
            placeholder="Jane Smith"
            value={form.name}
            onChange={set('name')}
            autoComplete="name"
            required
          />
        </div>
        <div className="auth-field auth-rise" style={rise(5)}>
          <label className="auth-label" htmlFor="email">Work email</label>
          <input
            id="email"
            className="auth-input"
            type="email"
            placeholder="jane@acmecorp.com"
            value={form.email}
            onChange={set('email')}
            autoComplete="email"
            required
          />
        </div>
        <div className="auth-field auth-rise" style={rise(6)}>
          <label className="auth-label" htmlFor="password">Password</label>
          <div className="auth-input-wrap">
            <input
              id="password"
              className="auth-input has-toggle"
              type={showPassword ? 'text' : 'password'}
              placeholder="Min. 6 characters"
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="auth-toggle"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <button className="auth-submit auth-rise" style={rise(7)} type="submit" disabled={loading}>
          {loading ? 'Creating account…' : 'Create free account'}
        </button>
      </form>

      <p className="auth-alt auth-rise" style={rise(8)}>
        Already have an account?{' '}
        <button type="button" className="auth-alt-link" onClick={onSwitch}>
          Log in
        </button>
      </p>
    </>
  );
}
